import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Bluetooth as BluetoothIcon,
  LocationOn as LocationIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TrackingBase = ({
  title,
  data,
  loading,
  onRefresh,
  onCreateItem,
  onEditItem,
  onDeleteItem,
  onAssociateBeacon,
  onViewLocation,
  onViewHistory,
  onConfigureRestrictions,
  renderTableColumns,
  renderTableRow,
  renderCreateEditForm,
  renderBeaconForm,
  renderRestrictionsForm,
  getMetrics,
  availableDevices = [],
  filters,
  onFiltersChange,
  renderFilters,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const metrics = getMetrics ? getMetrics(data) : {};

  const handleOpenDialog = (type, item = null) => {
    setDialogType(type);
    setSelectedItem(item);
    setTabValue(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setTabValue(0);
  };

  const handleSubmit = async (formData) => {
    try {
      if (dialogType === 'create') {
        await onCreateItem(formData);
        setSnackbar({ open: true, message: 'Item criado com sucesso', severity: 'success' });
      } else if (dialogType === 'edit') {
        await onEditItem(selectedItem.id, formData);
        setSnackbar({ open: true, message: 'Item atualizado com sucesso', severity: 'success' });
      } else if (dialogType === 'beacon') {
        await onAssociateBeacon(selectedItem.id, formData);
        setSnackbar({ open: true, message: 'Dispositivo associado com sucesso', severity: 'success' });
      } else if (dialogType === 'restrictions') {
        await onConfigureRestrictions(selectedItem.id, formData);
        setSnackbar({ open: true, message: 'Configurações atualizadas com sucesso', severity: 'success' });
      }
      
      handleCloseDialog();
      onRefresh();
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbar({ open: true, message: 'Erro ao salvar dados', severity: 'error' });
    }
  };



  const handleDelete = async (itemId) => {
    if (window.confirm('Tem certeza que deseja excluir este item?')) {
      try {
        await onDeleteItem(itemId);
        setSnackbar({ open: true, message: 'Item excluído com sucesso', severity: 'success' });
        onRefresh();
      } catch (error) {
        console.error('Error deleting item:', error);
        setSnackbar({ open: true, message: 'Erro ao excluir item', severity: 'error' });
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {title}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('create')}
        >
          Novo
        </Button>
      </Box>

      {/* Métricas */}
      {Object.keys(metrics).length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {Object.entries(metrics).map(([key, metric]) => (
            <Grid item xs={12} sm={6} md={3} key={key}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    {metric.label}
                  </Typography>
                  <Typography variant="h4" color={metric.color || 'text.primary'}>
                    {metric.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filtros */}
      {renderFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            {renderFilters(filters, onFiltersChange)}
          </CardContent>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title} em Tempo Real
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {renderTableColumns()}
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      Nenhum item encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((item) => (
                    <TableRow key={item.id}>
                      {renderTableRow(item)}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => handleOpenDialog('edit', item)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Dispositivo BLE">
                            <IconButton size="small" onClick={() => handleOpenDialog('beacon', item)}>
                              <BluetoothIcon />
                            </IconButton>
                          </Tooltip>
                          {onViewLocation && (
                            <Tooltip title="Localização">
                              <IconButton size="small" onClick={() => onViewLocation(item.id)}>
                                <LocationIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onViewHistory && (
                            <Tooltip title="Histórico">
                              <IconButton size="small" onClick={() => onViewHistory(item.id)}>
                                <HistoryIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onConfigureRestrictions && (
                            <Tooltip title="Configurações">
                              <IconButton size="small" onClick={() => handleOpenDialog('restrictions', item)}>
                                <WarningIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Excluir">
                            <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog Universal */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogType === 'create' && `Novo ${title.slice(0, -1)}`}
          {dialogType === 'edit' && `Editar ${title.slice(0, -1)}`}
          {dialogType === 'beacon' && 'Associar Dispositivo BLE'}
          {dialogType === 'restrictions' && 'Configurações Avançadas'}
        </DialogTitle>
        <DialogContent>
          {(dialogType === 'create' || dialogType === 'edit') && (
            <>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label="Informações Básicas" />
                <Tab label="Configurações RTLS" />
              </Tabs>
              <TabPanel value={tabValue} index={0}>
                {renderCreateEditForm && renderCreateEditForm(selectedItem, 'basic', handleSubmit)}
              </TabPanel>
              <TabPanel value={tabValue} index={1}>
                {renderCreateEditForm && renderCreateEditForm(selectedItem, 'rtls', handleSubmit)}
              </TabPanel>
            </>
          )}
          
          {dialogType === 'beacon' && renderBeaconForm && (
            renderBeaconForm(selectedItem, availableDevices, handleSubmit)
          )}
          
          {dialogType === 'restrictions' && renderRestrictionsForm && (
            renderRestrictionsForm(selectedItem, handleSubmit)
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" type="submit" form="tracking-form">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TrackingBase;